export const ANNOTATION_ATTACHMENT_LIMITS = Object.freeze({
  maxCount: 3,
  maxTotalBytes: 100 * 1024 * 1024,
  maxBytesByKind: Object.freeze({
    image: 10 * 1024 * 1024,
    video: 100 * 1024 * 1024,
    audio: 20 * 1024 * 1024
  })
})

export function createAnnotationUploadSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.random() * 16 | 0
    const value = character === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

export function normalizeAnnotationTextColor(value) {
  const color = String(value || '').trim()
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '#ffffff'
}

export function normalizeAnnotationTextSize(value) {
  const size = Number(value)
  return Number.isFinite(size) ? Math.min(32, Math.max(10, Math.round(size))) : 14
}

export function normalizeAnnotationCameraPose(value) {
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  const position = value?.position || value?.cameraPosition || value?.camera_position
  const focus = value?.focus || value?.target || value?.lookAt || value?.look_at
  const numbers = [position?.x, position?.y, position?.z, focus?.x, focus?.y, focus?.z].map(Number)
  if (!numbers.every(Number.isFinite)) return null
  return {
    position: { x: numbers[0], y: numbers[1], z: numbers[2] },
    focus: { x: numbers[3], y: numbers[4], z: numbers[5] }
  }
}

export function normalizeAnnotationAttachmentPosition(value) {
  const position = Number(value)
  return Number.isInteger(position) && position >= 0 && position < 3 ? position : null
}

export function inferAnnotationAttachmentKind(kindValue, mimeValue, nameValue = '', urlValue = '') {
  const explicitKind = String(kindValue || '').toLowerCase()
  if (['image', 'video', 'audio'].includes(explicitKind)) return explicitKind
  const mime = String(mimeValue || '').toLowerCase()
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  const source = `${nameValue || ''} ${urlValue || ''}`.split('?')[0].split('#')[0].toLowerCase()
  if (/\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/.test(source)) return 'image'
  if (/\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/.test(source)) return 'video'
  if (/\.(mp3|wav|m4a|aac|ogg|oga|flac|opus|weba|amr)$/.test(source)) return 'audio'
  return 'file'
}

export function normalizeAnnotationAttachmentLayout(value) {
  const attachments = (Array.isArray(value) ? value : [])
    .filter(item => item && normalizeAnnotationAttachmentPosition(item.position) !== null)
    .sort((left, right) => left.position - right.position)
    .slice(0, 3)
  if (attachments.length <= 1) return attachments.map(item => ({ ...item, position: 0 }))
  if (attachments.length === 2) {
    const bottom = attachments.find(item => item.position === 2)
    if (bottom) {
      const top = attachments.find(item => item !== bottom)
      return [{ ...top, position: 0 }, { ...bottom, position: 2 }]
    }
    return attachments.map((item, index) => ({ ...item, position: index }))
  }
  return attachments.map((item, index) => ({ ...item, position: index }))
}

export function normalizeAnnotationAttachments(value) {
  const usedPositions = new Set()
  const positioned = []
  const pending = []
  ;(Array.isArray(value) ? value : [])
    .filter(item => item && (
      item.url || item.src || item.dataUrl || item.fileUrl || item.file_url ||
      item.signedUrl || item.signed_url || item.downloadUrl || item.download_url ||
      item.file || item.fileId || item.file_id
    ))
    .map(item => {
      const mime = String(item.mime || item.type || item.contentType || item.content_type || item.file?.type || '').toLowerCase()
      const url = item.url || item.src || item.dataUrl || item.fileUrl || item.file_url ||
        item.signedUrl || item.signed_url || item.downloadUrl || item.download_url || ''
      const name = item.name || item.fileName || item.file_name || item.file?.name || 'attachment'
      const kind = inferAnnotationAttachmentKind(item.kind, mime, name, url)
      const uploadStatus = item.uploadStatus || (item.file ? 'pending' : url ? 'saved' : 'pending')
      const rawProgress = Number(item.uploadProgress)
      return {
        fileId: item.fileId || item.file_id || undefined,
        storageKey: item.storageKey || item.storage_key || undefined,
        name,
        size: Number(item.size ?? item.file?.size) || 0,
        mime,
        kind,
        url,
        position: normalizeAnnotationAttachmentPosition(item.position ?? item.slot),
        file: typeof File !== 'undefined' && item.file instanceof File ? item.file : undefined,
        uploadStatus,
        uploadProgress: Number.isFinite(rawProgress)
          ? Math.max(0, Math.min(100, Math.round(rawProgress)))
          : uploadStatus === 'saved' ? 100 : 0,
        uploadError: item.uploadError || ''
      }
    })
    .forEach(item => {
      if (item.position !== null && !usedPositions.has(item.position)) {
        usedPositions.add(item.position)
        positioned.push(item)
      } else {
        pending.push(item)
      }
    })
  pending.forEach(item => {
    const nextPosition = [0, 1, 2].find(position => !usedPositions.has(position))
    if (typeof nextPosition === 'undefined') return
    usedPositions.add(nextPosition)
    positioned.push({ ...item, position: nextPosition })
  })
  return normalizeAnnotationAttachmentLayout(positioned)
}

export function compactAnnotationAttachmentPositions(value) {
  return normalizeAnnotationAttachments(value).map((item, index) => ({ ...item, position: index }))
}

export function createPendingAnnotationAttachment(file) {
  return {
    name: file.name,
    size: file.size,
    mime: file.type,
    kind: inferAnnotationAttachmentKind('', file.type, file.name),
    url: URL.createObjectURL(file),
    file,
    uploadStatus: 'pending',
    uploadProgress: 0,
    uploadError: ''
  }
}

export function validateAnnotationAttachmentFiles(files, existing = []) {
  const list = Array.from(files || [])
  if (existing.length + list.length > ANNOTATION_ATTACHMENT_LIMITS.maxCount) {
    return `每个标注最多添加 ${ANNOTATION_ATTACHMENT_LIMITS.maxCount} 个附件`
  }
  for (const file of list) {
    const kind = inferAnnotationAttachmentKind('', file.type, file.name)
    if (!['image', 'video', 'audio'].includes(kind)) return `不支持附件“${file.name}”的格式`
    const maxBytes = ANNOTATION_ATTACHMENT_LIMITS.maxBytesByKind[kind]
    if (file.size > maxBytes) return `“${file.name}”不能超过 ${Math.round(maxBytes / 1024 / 1024)} MB`
  }
  const total = [
    ...existing.map(item => Number(item?.size) || 0),
    ...list.map(file => file.size)
  ].reduce((sum, size) => sum + size, 0)
  if (total > ANNOTATION_ATTACHMENT_LIMITS.maxTotalBytes) return '附件总大小不能超过 100 MB'
  return ''
}

export function formatAnnotationAttachmentSize(value) {
  const bytes = Math.max(0, Number(value) || 0)
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

export function revokeLocalAnnotationAttachments(value) {
  normalizeAnnotationAttachments(value).forEach(item => {
    if (String(item.url || '').startsWith('blob:')) URL.revokeObjectURL(item.url)
  })
}

export function annotationAttachmentForServer(item) {
  return {
    fileId: item.fileId,
    storageKey: item.storageKey,
    name: item.name,
    size: Number(item.size) || 0,
    mime: item.mime,
    kind: item.kind,
    position: normalizeAnnotationAttachmentPosition(item.position) ?? 0,
    url: item.url
  }
}
